export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'packed'
  | 'creating'
  | 'shipped'
  | 'delivered'
  | 'installed'
  | 'closed'
  | 'cancelled'
  | 'returned';

export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export type PaymentMethod = 'cod' | 'razorpay' | 'stripe' | 'bank_transfer';

export type OrderStatusFilter = 'all' | OrderStatus;
export type PaymentStatusFilter = 'all' | PaymentStatus;
export type PaymentMethodFilter = 'all' | PaymentMethod;

export type OrderItemStatus = 'active' | 'cancelled' | 'returned';

export interface OrderItemAttribute {
  name: string;
  value: string;
}

export interface OrderItem {
  id: string;
  orderId: string;
  productId: string;
  variantId?: string;
  productName: string;
  productSlug: string;
  sku: string;
  image?: string;
  attributes: OrderItemAttribute[];
  quantity: number;
  mrp: number;
  salePrice: number;
  discount: number;
  tax: number;
  total: number;
  priceOrigin?: 'india' | 'other-than-india' | 'saarc';
  status: OrderItemStatus;
  createdAt: string;
  updatedAt: string;
}

export interface OrderShippingInfo {
  id: string;
  orderId: string;
  customerName: string;
  customerEmail?: string;
  customerPhone: string;
  shippingName: string;
  shippingPhone: string;
  shippingAddress1: string;
  shippingAddress2?: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingPostalCode: string;
  billingName: string;
  billingPhone: string;
  billingAddress1: string;
  billingAddress2?: string;
  billingCity: string;
  billingState: string;
  billingCountry: string;
  billingPostalCode: string;
  courierName?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  deliveryInstructions?: string;
  dispatchMode?: string;
  eflipSentOn?: string;
  downloadLimit?: string;
  downloadUniqueKey?: string;
  eflipUsername?: string;
  eflipPassword?: string;
  createdAt: string;
  updatedAt: string;
}

export type OrderFulfillmentKind = 'print' | 'eflipbook' | 'webbased';

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  totalItems: number;
  totalQuantity: number;
  subtotal: number;
  discountAmount: number;
  couponCode?: string;
  shippingCharge: number;
  taxAmount: number;
  grandTotal: number;
  priceOrigin?: 'india' | 'other-than-india' | 'saarc';
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  orderStatus: OrderStatus;
  /** Format of the order items: Print, eFlipBook, or Web Based. */
  fulfillmentKind?: OrderFulfillmentKind;
  note?: string;
  placedAt: string;
  confirmedAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  stockDeducted?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrderDetail extends Order {
  details: OrderShippingInfo | null;
  items: OrderItem[];
}

export interface PaginatedOrders {
  results: Order[];
  total: number;
  page: string | number;
  limit: string | number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface OrderListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  customerId?: string;
  orderStatus?: OrderStatus;
  paymentStatus?: PaymentStatus;
  paymentMethod?: PaymentMethod;
  dateFrom?: string;
  dateTo?: string;
}

export interface UpdateOrderStatusPayload {
  orderStatus?: OrderStatus;
  paymentStatus?: PaymentStatus;
  dispatchMode?: 'by_hand' | 'courier' | 'speed_post';
  courierName?: string;
  trackingNumber?: string;
  shippedAt?: string;
  eflipSentOn?: string;
  downloadLimit?: string;
  downloadUniqueKey?: string;
  eflipUsername?: string;
  eflipPassword?: string;
  /** When true, send fulfillment email (dispatch / eFlip / web credentials). */
  sendEmail?: boolean;
}

/** Default / list labels (print-oriented). Detail page uses format-specific options. */
export const ORDER_STATUS_OPTIONS = [
  { label: 'Order Placed', value: 'pending' as const },
  { label: 'Order Accepted', value: 'confirmed' as const },
  { label: 'Under Preparation', value: 'processing' as const },
  { label: 'Dispatch Awaited', value: 'packed' as const },
  { label: 'Under Creation', value: 'creating' as const },
  { label: 'Dispatched', value: 'shipped' as const },
  { label: 'Received', value: 'delivered' as const },
  { label: 'eFlipBook Installed', value: 'installed' as const },
  { label: 'Confirmed & Closed', value: 'closed' as const },
  { label: 'Cancelled', value: 'cancelled' as const },
  { label: 'Returned', value: 'returned' as const },
];

export const DISPATCH_MODE_OPTIONS = [
  { label: 'By Hand', value: 'by_hand' as const },
  { label: 'Courier', value: 'courier' as const },
  { label: 'Speed Post', value: 'speed_post' as const },
];

export const DISPATCH_BY_OPTIONS = [
  'DTDC',
  'DHL',
  'Government Speed Post',
  'By Hand',
  'Other',
];

export const PAYMENT_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' as const },
  { label: 'Paid', value: 'paid' as const },
  { label: 'Failed', value: 'failed' as const },
  { label: 'Refunded', value: 'refunded' as const },
];

export const PAYMENT_METHOD_OPTIONS = [
  { label: 'COD', value: 'cod' as const },
  { label: 'Razorpay', value: 'razorpay' as const },
  { label: 'Stripe', value: 'stripe' as const },
  { label: 'Bank transfer', value: 'bank_transfer' as const },
];
